import { Stack, useMediaQuery, useTheme } from "@mui/material";
import { BaseButton } from "@ui/atoms/BaseButton/BaseButton";
import BaseTypography from "@ui/atoms/BaseTypography/BaseTypography";
import Logo from "@ui/atoms/Logo/Logo";
import Icon from "@ui/atoms/Icon/Icon";

// Deliberately free of react-router hooks. This renders as the top-level
// ErrorBoundary's fallback (ErrorBoundary.tsx), which sits *outside*
// RouterProvider in App.tsx — so useNavigate() threw the "may be used only in
// the context of a <Router>" invariant, uncaught, and the user got a blank
// screen instead of this page. Worse, that crash replaced the original error
// in Sentry with a message-less one (FRONTEND-ZX), losing what actually broke.
// window.history.back() is the same browser operation navigate(-1) performs.
const ErrorPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleGoHome = () => {
    window.location.href = "/";
  };

  const handleGoBack = () => {
    // The reload is load-bearing, not belt-and-braces: ErrorBoundary latches on
    // state.error and has no reset path, so a history change alone leaves the
    // user staring at this page with a new URL. Only a document load clears it.
    // back() is async and reload() is not, so in practice this refreshes in
    // place rather than going back — the lesser evil, and unchanged from
    // navigate(-1). Don't drop the reload to "fix" the race.
    window.history.back();
    window.location.reload();
  };

  return (
    <Stack
      sx={{
        alignItems: "center",
        background: `linear-gradient(180deg, ${theme.vars.palette.grey[200]} 0%, ${theme.vars.palette.grey[100]} 100%)`,
        justifyContent: "center",
        minHeight: "100vh",
        px: 3,
        py: 6
      }}
    >
      <Stack
        sx={{
          alignItems: "center",
          maxWidth: 600,
          textAlign: "center",
          width: "100%"
        }}
      >
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            justifyContent: "center",
            mb: { sm: 5, xs: 3 }
          }}
        >
          <Logo
            variant="mark"
            width={isMobile ? 56 : 80}
            startColor={theme.vars.ui.logo.gradient.from}
            endColor={theme.vars.ui.logo.gradient.to}
          />
        </Stack>

        <BaseTypography
          variant="h1"
          weight="semiBold"
          sx={{
            color: (theme) => theme.vars.palette.text.tertiary,
            fontSize: { md: "3.375rem", sm: "2.5rem", xs: "1.75rem" },
            mb: { sm: 4, xs: 2 }
          }}
          text="Something went wrong."
        />

        <BaseTypography
          variant="h3"
          weight="regular"
          sx={{
            color: (theme) => theme.vars.palette.text.secondary,
            fontSize: { sm: "1.25rem", xs: "0.9375rem" }
          }}
          text="Give the page a refresh. If that doesn't fix it,"
        />

        <BaseTypography
          variant="h3"
          weight="regular"
          sx={{
            color: (theme) => theme.vars.palette.text.secondary,
            fontSize: { sm: "1.25rem", xs: "0.9375rem" }
          }}
          text="get in touch with support."
        />

        <Stack
          direction={{ sm: "row", xs: "column" }}
          spacing={2}
          sx={{
            alignItems: "center",
            justifyContent: "center",
            mt: { sm: 7, xs: 5 },
            width: { sm: "auto", xs: "100%" }
          }}
        >
          <BaseButton
            onClick={handleGoBack}
            color="secondary"
            text="Go Back"
            startIcon={<Icon name="chevron-left" size="sm" />}
            fullWidth={isMobile}
            sx={{ width: { sm: "auto", xs: "100%" } }}
          />
          <BaseButton
            onClick={handleGoHome}
            text="Return Home"
            startIcon={<Icon name="home" size="sm" />}
            fullWidth={isMobile}
            sx={{ width: { sm: "auto", xs: "100%" } }}
          />
        </Stack>
      </Stack>
    </Stack>
  );
};

export default ErrorPage;
